import React from 'react';
import './MemberDetailPopup.css';

const MemberDetailPopup = ({ member, isOpen, onClose }) => {
  if (!isOpen || !member) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status, type = 'default') => {
    const statusMap = {
      active: { class: 'active', label: 'Active', color: '#38a169' },
      inactive: { class: 'inactive', label: 'Inactive', color: '#a0aec0' },
      suspended: { class: 'suspended', label: 'Suspended', color: '#e53e3e' },
      expired: { class: 'expired', label: 'Expired', color: '#e53e3e' },
      pending: { class: 'pending', label: 'Pending', color: '#d69e2e' },
      approved: { class: 'approved', label: 'Approved', color: '#38a169' },
      rejected: { class: 'rejected', label: 'Rejected', color: '#e53e3e' },
      needs_info: { class: 'needs_info', label: 'Needs Info', color: '#d69e2e' },
      current: { class: 'current', label: 'Current', color: '#38a169' },
      overdue: { class: 'overdue', label: 'Overdue', color: '#e53e3e' },
      synced: { class: 'synced', label: 'Synced', color: '#38a169' },
      failed: { class: 'failed', label: 'Failed', color: '#e53e3e' },
      not_synced: { class: 'not_synced', label: 'Not Synced', color: '#a0aec0' }
    };

    const statusInfo = statusMap[status] || { class: 'unknown', label: status, color: '#a0aec0' };
    return (
      <span
        className={`status-badge ${statusInfo.class}`}
        style={{ backgroundColor: `${statusInfo.color}20`, color: statusInfo.color }}
      >
        {statusInfo.label}
      </span>
    );
  };

  const formatArray = (arr) => {
    if (!arr || !Array.isArray(arr)) return 'N/A';
    return arr.join(', ');
  };

  const formatBoolean = (value) => {
    return value ? 'Yes' : 'No';
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>Member Details</h2>
          <button
            className="modal-close-btn"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="member-detail-container">
            <div className="member-info-grid">
              {/* Basic Information Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-icon">🏢</div>
                  <h4 className="card-title">Basic Information</h4>
                </div>
                <div className="card-content">
                  <div className="info-item">
                    <span className="info-label">Member ID:</span>
                    <span className="info-value member-id">{member.member_id || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Organization Name:</span>
                    <span className="info-value">{member.organization_name || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Excel ID:</span>
                    <span className="info-value">{member.excel_id || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Excel Status:</span>
                    <span className="info-value">{member.excel_status || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Current Members Count:</span>
                    <span className="info-value">{member.current_members_count || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Contact Information Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-icon">👤</div>
                  <h4 className="card-title">Contact Information</h4>
                </div>
                <div className="card-content">
                  <div className="info-item">
                    <span className="info-label">Contact Person:</span>
                    <span className="info-value">
                      {member.primary_contact_name || 'N/A'}
                      {member.contact_person_display ? ` (${member.contact_person_display})` : ''}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Contact Email:</span>
                    <span className="info-value">
                      {member.primary_contact_email ? (
                        <a href={`mailto:${member.primary_contact_email}`} className="value-link">
                          {member.primary_contact_email}
                        </a>
                      ) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Contact Phone:</span>
                    <span className="info-value">
                      {member.primary_contact_phone ? (
                        <a href={`tel:${member.primary_contact_phone}`} className="value-link">
                          {member.primary_contact_phone}
                        </a>
                      ) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Applicant Role:</span>
                    <span className="info-value">{member.applicant_role || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Executive Information Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-icon">👔</div>
                  <h4 className="card-title">Executive Information</h4>
                </div>
                <div className="card-content">
                  <div className="info-item">
                    <span className="info-label">President Name:</span>
                    <span className="info-value">{member.president_name || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">President Email:</span>
                    <span className="info-value">
                      {member.president_email ? (
                        <a href={`mailto:${member.president_email}`} className="value-link">
                          {member.president_email}
                        </a>
                      ) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">President Phone:</span>
                    <span className="info-value">
                      {member.president_phone ? (
                        <a href={`tel:${member.president_phone}`} className="value-link">
                          {member.president_phone}
                        </a>
                      ) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Secretary Name:</span>
                    <span className="info-value">{member.secretary_name || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Secretary Email:</span>
                    <span className="info-value">
                      {member.secretary_email ? (
                        <a href={`mailto:${member.secretary_email}`} className="value-link">
                          {member.secretary_email}
                        </a>
                      ) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Secretary Phone:</span>
                    <span className="info-value">
                      {member.secretary_phone ? (
                        <a href={`tel:${member.secretary_phone}`} className="value-link">
                          {member.secretary_phone}
                        </a>
                      ) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Treasurer Name:</span>
                    <span className="info-value">{member.treasurer_name || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Treasurer Email:</span>
                    <span className="info-value">
                      {member.treasurer_email ? (
                        <a href={`mailto:${member.treasurer_email}`} className="value-link">
                          {member.treasurer_email}
                        </a>
                      ) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Treasurer Phone:</span>
                    <span className="info-value">
                      {member.treasurer_phone ? (
                        <a href={`tel:${member.treasurer_phone}`} className="value-link">
                          {member.treasurer_phone}
                        </a>
                      ) : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Location Information Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-icon">📍</div>
                  <h4 className="card-title">Location Information</h4>
                </div>
                <div className="card-content">
                  <div className="info-item">
                    <span className="info-label">Street Address 1:</span>
                    <span className="info-value">{member.street_address_1 || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Street Address 2:</span>
                    <span className="info-value">{member.street_address_2 || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">City:</span>
                    <span className="info-value">{member.city || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">State:</span>
                    <span className="info-value">{member.state || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">State Display:</span>
                    <span className="info-value">{member.state_display || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Postcode:</span>
                    <span className="info-value">{member.postcode || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Country:</span>
                    <span className="info-value">{member.country || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Membership Information Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-icon">📋</div>
                  <h4 className="card-title">Membership Information</h4>
                </div>
                <div className="card-content">
                  <div className="info-item">
                    <span className="info-label">Status:</span>
                    <span className="info-value">{getStatusBadge(member.status)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Membership Status:</span>
                    <span className="info-value">{getStatusBadge(member.membership_status)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Is Active:</span>
                    <span className="info-value">{formatBoolean(member.is_active)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Membership Start Date:</span>
                    <span className="info-value">{formatDate(member.membership_start_date)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Membership End Date:</span>
                    <span className="info-value">{formatDate(member.membership_end_date)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email Subscribed:</span>
                    <span className="info-value">{formatBoolean(member.email_subscribed)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Renewal:</span>
                    <span className="info-value">{formatBoolean(member.renewal)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Agreed to Conditions:</span>
                    <span className="info-value">{formatBoolean(member.agreed_to_conditions)}</span>
                  </div>
                </div>
              </div>

              {/* Screening Information Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-icon">🎬</div>
                  <h4 className="card-title">Screening Information</h4>
                </div>
                <div className="card-content">
                  <div className="info-item">
                    <span className="info-label">Screening Frequency:</span>
                    <span className="info-value">{member.screening_frequency || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Screening Format:</span>
                    <span className="info-value">{formatArray(member.screening_format)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Screening Venue:</span>
                    <span className="info-value">{member.screening_venue || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Financial Information Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-icon">💰</div>
                  <h4 className="card-title">Financial Information</h4>
                </div>
                <div className="card-content">
                  <div className="info-item">
                    <span className="info-label">Payment Status:</span>
                    <span className="info-value">{getStatusBadge(member.payment_status)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Outstanding Balance:</span>
                    <span className="info-value">${member.outstanding_balance || '0.00'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Last Invoice Date:</span>
                    <span className="info-value">{formatDate(member.last_invoice_date)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Last Payment Date:</span>
                    <span className="info-value">{formatDate(member.last_payment_date)}</span>
                  </div>
                </div>
              </div>

              {/* Integration Status Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-icon">🔗</div>
                  <h4 className="card-title">Integration Status</h4>
                </div>
                <div className="card-content">
                  <div className="info-item">
                    <span className="info-label">Sync Status:</span>
                    <span className="info-value">{getStatusBadge(member.sync_status)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Last Synced At:</span>
                    <span className="info-value">{formatDateTime(member.last_synced_at)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Sync Error Message:</span>
                    <span className="info-value">{member.sync_error_message || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Google Sheets Row ID:</span>
                    <span className="info-value">{member.google_sheets_row_id || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">MailChimp Member ID:</span>
                    <span className="info-value">{member.mailchimp_member_id || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">MailChimp Sync Status:</span>
                    <span className="info-value">{getStatusBadge(member.mailchimp_sync_status)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">MailChimp Sync Error:</span>
                    <span className="info-value">{member.mailchimp_sync_error || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">MailChimp Synced At:</span>
                    <span className="info-value">{formatDateTime(member.mailchimp_synced_at)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">MailChimp Last Updated:</span>
                    <span className="info-value">{formatDateTime(member.mailchimp_last_updated)}</span>
                  </div>
                </div>
              </div>

              {/* Website & Social Media Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-icon">🌐</div>
                  <h4 className="card-title">Website & Social Media</h4>
                </div>
                <div className="card-content">
                  <div className="info-item">
                    <span className="info-label">Website URL:</span>
                    <span className="info-value">
                      {member.website_url ? (
                        <a
                          href={member.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="value-link"
                        >
                          {member.website_url}
                        </a>
                      ) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Facebook URL:</span>
                    <span className="info-value">
                      {member.facebook_url ? (
                        <a
                          href={member.facebook_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="value-link"
                        >
                          {member.facebook_url}
                        </a>
                      ) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Instagram URL:</span>
                    <span className="info-value">
                      {member.instagram_url ? (
                        <a
                          href={member.instagram_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="value-link"
                        >
                          {member.instagram_url}
                        </a>
                      ) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Twitter URL:</span>
                    <span className="info-value">
                      {member.twitter_url ? (
                        <a
                          href={member.twitter_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="value-link"
                        >
                          {member.twitter_url}
                        </a>
                      ) : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Preferences & Permissions Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-icon">⚙️</div>
                  <h4 className="card-title">Preferences & Permissions</h4>
                </div>
                <div className="card-content">
                  <div className="info-item">
                    <span className="info-label">Release to Third Parties:</span>
                    <span className="info-value">{formatBoolean(member.release_to_third_parties)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Publish on Website:</span>
                    <span className="info-value">{formatBoolean(member.publish_on_website)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Show in External View:</span>
                    <span className="info-value">{formatBoolean(member.show_in_external_view)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Allow Distributor Access:</span>
                    <span className="info-value">{formatBoolean(member.allow_distributor_access)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email Preferences:</span>
                    <span className="info-value">
                      {member.email_preferences && Object.keys(member.email_preferences).length > 0
                        ? JSON.stringify(member.email_preferences)
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Notes Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-icon">📝</div>
                  <h4 className="card-title">Notes</h4>
                </div>
                <div className="card-content">
                  <div className="info-item">
                    <span className="info-label">Admin Notes:</span>
                    <span className="info-value">{member.admin_notes || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Public Notes:</span>
                    <span className="info-value">{member.public_notes || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* System Information Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-icon">🔧</div>
                  <h4 className="card-title">System Information</h4>
                </div>
                <div className="card-content">
                  <div className="info-item">
                    <span className="info-label">ID:</span>
                    <span className="info-value">{member.id || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">JotForm Submission ID:</span>
                    <span className="info-value">{member.jotform_submission_id || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Force Update No:</span>
                    <span className="info-value">{member.force_update_no || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Full Edit Link:</span>
                    <span className="info-value">
                      {member.full_edit_link ? (
                        <a
                          href={member.full_edit_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="value-link"
                        >
                          Edit Link
                        </a>
                      ) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Raw Edit Link:</span>
                    <span className="info-value">
                      {member.raw_edit_link ? (
                        <a
                          href={member.raw_edit_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="value-link"
                        >
                          Raw Edit Link
                        </a>
                      ) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Created At:</span>
                    <span className="info-value">{formatDateTime(member.created_at)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Updated At:</span>
                    <span className="info-value">{formatDateTime(member.updated_at)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Reviewed At:</span>
                    <span className="info-value">{formatDateTime(member.reviewed_at)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Reviewed By:</span>
                    <span className="info-value">{member.reviewed_by || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Created By:</span>
                    <span className="info-value">{member.created_by || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Financial Period:</span>
                    <span className="info-value">{member.financial_period || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemberDetailPopup;